import { useState } from "react";
import { useApi } from "../useApi.js";
import { api, euro, dateDe } from "../api.js";
import type { TicketRow, DeviceRow, ShippingLabel } from "../types.js";
import { Page, Pill } from "../components/Shell.js";
import { toast } from "../toast.js";

interface ServiceItem { code: string; name: string; priceCents: number; loaner: boolean }

const STATE_LABEL: Record<string, string> = {
  REQUESTED: "Angefordert", PICKUP: "Abholung", IN_SERVICE: "In Service",
  RETURN: "Rückversand", DONE: "Abgeschlossen",
};

export default function Service() {
  const tickets = useApi<TicketRow[]>("/api/service/tickets");
  const catalog = useApi<ServiceItem[]>("/api/service/catalog");
  const devices = useApi<DeviceRow[]>("/api/devices");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ deviceId: "", serviceCode: "A44", note: "" });
  const [label, setLabel] = useState<ShippingLabel | null>(null);
  const [labelBusy, setLabelBusy] = useState<string | null>(null);

  async function create() {
    if (!form.deviceId) { toast("Bitte ein Gerät auswählen.", "info"); return; }
    setBusy(true);
    try {
      await api.post("/api/service/tickets", {
        deviceId: form.deviceId, serviceCode: form.serviceCode,
        note: form.note || "Ohne nähere Angabe",
      });
      setOpen(false);
      setForm({ deviceId: "", serviceCode: "A44", note: "" });
      await tickets.reload();
      toast("Serviceauftrag angelegt", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Anlegen fehlgeschlagen", "error");
    } finally { setBusy(false); }
  }

  async function advance(id: string) {
    setBusy(true);
    try { await api.post(`/api/service/tickets/${id}/advance`); await tickets.reload(); }
    catch (e) { toast(e instanceof Error ? e.message : "Statuswechsel fehlgeschlagen", "error"); }
    finally { setBusy(false); }
  }

  async function createLabel(id: string) {
    setLabelBusy(id);
    try {
      const l = await api.post<ShippingLabel>(`/api/service/tickets/${id}/label`);
      setLabel(l);
      await tickets.reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Versandlabel konnte nicht erstellt werden", "error");
    } finally { setLabelBusy(null); }
  }

  const list = tickets.data ?? [];

  return (
    <Page title="Service & Prüfungen"
          sub={`${list.filter((t) => t.state !== "DONE").length} offene Aufträge`}
          actions={<button className="btn pri" onClick={() => setOpen(true)}>Serviceauftrag anlegen</button>}>

      {open && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-h"><h3>Neuer Serviceauftrag</h3></div>
          <div className="card-b">
            <div className="field">
              <label htmlFor="dev">Gerät</label>
              <select id="dev" className="srch" style={{ width: "100%" }}
                      value={form.deviceId} onChange={(e) => setForm({ ...form, deviceId: e.target.value })}>
                <option value="">Bitte wählen…</option>
                {(devices.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{d.inventoryNo} — {d.crew}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="svc">Leistung</label>
              <select id="svc" className="srch" style={{ width: "100%" }}
                      value={form.serviceCode} onChange={(e) => setForm({ ...form, serviceCode: e.target.value })}>
                {(catalog.data ?? []).map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name} — {euro(s.priceCents)}{s.loaner ? " · mit Leihgerät" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="note">Grund / Befund</label>
              <input id="note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                     placeholder="z. B. Jahreswartung fällig, Dichtung undicht…" />
            </div>
            <div style={{ display: "flex", gap: 9, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setOpen(false)}>Abbrechen</button>
              <button className="btn pri" disabled={busy} onClick={() => void create()}>Auftrag anlegen</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-h"><h3>Laufende Serviceaufträge</h3>
          <span className="hint">Status wird vom Techniker fortgeschrieben</span></div>
        <div className="tbl-wrap">
          {list.length === 0
            ? <div className="empty">Kein Serviceauftrag angelegt.</div>
            : <table>
                <thead><tr><th>Auftrag</th><th>Gerät</th><th>Leistung</th><th>Grund</th>
                  <th className="r">Angelegt</th><th>Status</th><th>Versand</th><th /></tr></thead>
                <tbody>
                  {list.map((t) => (
                    <tr key={t.id}>
                      <td><b className="mono">{t.ticketNo}</b></td>
                      <td><b className="mono">{t.device.inventoryNo}</b>
                        <div className="sub-t">{t.device.crew}</div></td>
                      <td>{t.service?.name ?? t.serviceCode}
                        <div className="sub-t mono">Pos. {t.serviceCode}
                          {t.service ? ` · ${euro(t.service.priceCents)}` : ""}
                          {t.service?.loaner ? " · Leihgerät" : ""}</div></td>
                      <td className="sub-t" style={{ maxWidth: 260 }}>{t.note}</td>
                      <td className="r num">{dateDe(t.openedAt)}</td>
                      <td><Pill status={t.state === "DONE" ? "OK" : t.state === "IN_SERVICE" ? "info" : "mute"}>
                        {STATE_LABEL[t.state] ?? t.state}</Pill></td>
                      <td>
                        <button className="btn sm" disabled={labelBusy === t.id}
                                onClick={() => void createLabel(t.id)}>
                          {labelBusy === t.id ? "…" : t.labelCreatedAt ? "Label anzeigen" : "Label erstellen"}
                        </button>
                      </td>
                      <td className="r">
                        {t.state !== "DONE" && (
                          <button className="btn sm" disabled={busy} onClick={() => void advance(t.id)}>
                            Weiter
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>}
        </div>
      </div>

      {label && (
        <div className="modal-overlay" onClick={() => setLabel(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="card-h">
              <h3>Versandlabel</h3>
              <span className="pill mute">Exemplarisch</span>
            </div>
            <div className="card-b">
              <p className="sub-t" style={{ marginBottom: 14 }}>
                Platzhalter vor der DHL-Anbindung — es wird keine echte Sendung ausgelöst.
              </p>
              <dl className="dl" style={{ marginBottom: 14 }}>
                <dt>Sendungsnummer</dt><dd className="mono">{label.labelTrackingNo}</dd>
                <dt>Auftrag</dt><dd className="mono">{label.ticketNo}</dd>
                <dt>Gerät</dt><dd>{label.device.inventoryNo} · {label.device.serialNo}</dd>
                <dt>Erstellt</dt><dd>{dateDe(label.labelCreatedAt)}</dd>
              </dl>
              <div className="grid g2" style={{ gap: 14 }}>
                <div className="card" style={{ padding: 12 }}>
                  <div className="sub-t" style={{ marginBottom: 6 }}>Absender</div>
                  <b>{label.sender.name}</b>
                  <div>{label.sender.location}</div>
                </div>
                <div className="card" style={{ padding: 12 }}>
                  <div className="sub-t" style={{ marginBottom: 6 }}>Empfänger</div>
                  <b>{label.recipient.name}</b>
                  <div>{label.recipient.street}</div>
                  <div>{label.recipient.zip} {label.recipient.city}</div>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <button className="btn" onClick={() => setLabel(null)}>Schließen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
